import { useKeyboardShortcut } from '@bestseller-bit/frontend-community.hooks.use-keyboard-shortcut';
import { datadogRum } from '@datadog/browser-rum';
import { East } from '@mui/icons-material';
import { Box, Dialog, DialogContent, Drawer, Stack, styled, Typography } from '@mui/material';
import _ from 'lodash';
import { FC, useCallback, useState } from 'react';
import { useCurrentStoresList } from 'src/hooks/store/useCurrentStoresList';
import { useTurnoverSaveChanges } from 'src/hooks/turnover/save-changes/useTurnoverSaveChanges';
import { useDrawerState } from 'src/hooks/useDrawerState';
import { numberWithSpaces } from 'src/utils/numberWithSpaces';
import { DialogTitleWithCloseIcon } from '../../DialogTitleWithCloseIcon/DialogTitleWithCloseIcon';
import { BulkEditDrawerSection, BulkEditRow } from './BulkEditDrawerSection';

export const BulkEditDrawer: FC = () => {
    const { bulkEditDrawerOpen, setBulkEditDrawerOpen } = useDrawerState();
    const [modalOpen, setModalOpen] = useState(false);
    const [originalTotalSales, setOriginalTotalSales] = useState(0);
    const [updatedTotalSales, setUpdatedTotalSales] = useState(0);
    const [changes, setChanges] = useState<BulkEditRow[]>([]);
    const stores = useCurrentStoresList();

    const [saveChanges, { loading: isSaving }] = useTurnoverSaveChanges(changes);

    const handleCancel = useCallback(() => {
        setBulkEditDrawerOpen(false);
        datadogRum.addAction('bulk-edit', {
            action: 'close',
        });
    }, [setBulkEditDrawerOpen]);

    const handleShowDetails = () => {
        setModalOpen(true);
    };

    const handleSave = useCallback(() => {
        saveChanges().then(() => {
            if (changes.length > 0) {
                handleCancel();
            }
        });
    }, [changes, saveChanges, handleCancel]);

    const handleChanges = (updatedChanges: BulkEditRow[], originalSales: number, updatedSales: number) => {
        setChanges(updatedChanges);
        setOriginalTotalSales(Math.round(originalSales));
        setUpdatedTotalSales(Math.round(updatedSales));
    };

    const handleCloseDialog = () => setModalOpen(false);

    useKeyboardShortcut({
        keyCombo: {
            code: 'KeyV',
            altKey: true,
            description: 'Save changes in bulk edit mode',
            id: 'bulk-edit-save-changes',
        },
        callback: (event) => {
            event.preventDefault();
            event.stopPropagation();
            if (bulkEditDrawerOpen && changes.length > 0) {
                handleSave();
            }
        },
        disabled: !bulkEditDrawerOpen || isSaving,
    });

    useKeyboardShortcut({
        keyCombo: {
            code: 'KeyC',
            altKey: true,
            description: 'Cancel changes in bulk edit mode',
            id: 'bulk-edit-cancel-changes',
        },
        callback: (event) => {
            event.preventDefault();
            event.stopPropagation();
            if (bulkEditDrawerOpen) {
                handleCancel();
            }
        },
        disabled: !bulkEditDrawerOpen || isSaving,
    });

    return (
        <>
            <Drawer open={bulkEditDrawerOpen} onClose={handleCancel} anchor="right">
                <BulkEditDrawerSection
                    isSaving={isSaving}
                    onCancel={handleCancel}
                    onSave={handleSave}
                    onShowDetails={handleShowDetails}
                    onChanges={handleChanges}
                />
            </Drawer>
            <Dialog
                maxWidth="xs"
                fullWidth
                open={modalOpen}
                onClose={handleCloseDialog}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <DialogTitleWithCloseIcon title="Bulk Edit Details" handleClose={handleCloseDialog} />
                <DialogContent>
                    {originalTotalSales !== updatedTotalSales && (
                        <>
                            <Typography variant="subtitle1">Total Expected Sales iV</Typography>
                            <Stack
                                direction="row"
                                sx={{
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: 4,
                                }}
                            >
                                <SalesBox>
                                    <Typography sx={{ textDecoration: 'line-through' }} variant="subtitle1">
                                        {numberWithSpaces(originalTotalSales)}
                                    </Typography>
                                </SalesBox>
                                <East />
                                <SalesBox>
                                    <Typography variant="subtitle1">{numberWithSpaces(updatedTotalSales)}</Typography>
                                </SalesBox>
                            </Stack>
                        </>
                    )}
                    <Typography variant="subtitle1">Store list</Typography>
                    <Typography variant="body2" gutterBottom>
                        These stores will be affected by the changes you made in the bulk edit mode.
                    </Typography>
                    <Stack
                        sx={{
                            mt: 3,
                        }}
                    >
                        {_.sortBy(stores, ['storeName'])?.map((store) => (
                            <Typography key={store.id} variant="caption">
                                {store.storeName}
                            </Typography>
                        ))}
                    </Stack>
                </DialogContent>
            </Dialog>
        </>
    );
};

const SalesBox = styled(Box)(({ theme }) => ({
    padding: theme.spacing(4),
    margin: theme.spacing(1),
    backgroundColor: theme.palette.grey[200],
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
}));
